import { AlertTriangle, Info, Loader2 } from 'lucide-react';

interface InterfaceDependency {
  type: string;
  id: string;
  name: string;
  detail: string;
  severity: 'critical' | 'warning';
}

interface CascadingDependency {
  logicalInterfaceId: string;
  logicalInterfaceName: string;
  dependencies: InterfaceDependency[];
}

export interface InterfaceDependencyReport {
  interfaceId: string;
  interfaceName: string;
  interfaceType: 'physical' | 'logical';
  dependencies: InterfaceDependency[];
  summary: {
    total: number;
    critical: number;
    warning: number;
  };
  cascadingDependencies?: CascadingDependency[];
}

interface InterfaceDependencyWarningProps {
  report: InterfaceDependencyReport | null;
  loading: boolean;
  action: 'disable' | 'delete';
}

const TYPE_LABELS: Record<string, string> = {
  subscription: 'Subscriptions',
  static_route: 'Static Routes',
  acl: 'Access Control Lists',
  flowprobe: 'Flow Export (IPFIX)',
  wireguard_app: 'App VPN',
  dscp_routing: 'DSCP Routing Policies',
  service_chain: 'Service Chain',
  dhcp_server: 'DHCP Servers',
  snat: 'Source NAT',
  site_vpn: 'Site VPN Tunnels',
  gre_tunnel: 'GRE Tunnels',
  logical_interface: 'Logical Interfaces',
};

function groupByType(deps: InterfaceDependency[]): Record<string, InterfaceDependency[]> {
  const groups: Record<string, InterfaceDependency[]> = {};
  for (const dep of deps) {
    if (!groups[dep.type]) groups[dep.type] = [];
    groups[dep.type].push(dep);
  }
  return groups;
}

function DependencyItem({ dep }: { dep: InterfaceDependency }) {
  const isCritical = dep.severity === 'critical';
  return (
    <div className="flex items-start space-x-2 py-1">
      {isCritical ? (
        <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
      ) : (
        <Info className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
      )}
      <div className="text-sm">
        <span className="font-medium text-gray-900">{dep.name}</span>
        <span className="text-gray-500 ml-1">- {dep.detail}</span>
      </div>
    </div>
  );
}

function DependencyGroup({ deps }: { deps: InterfaceDependency[] }) {
  const grouped = groupByType(deps);
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([type, items]) => (
        <div key={type}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {TYPE_LABELS[type] || type} ({items.length})
          </h4>
          <div className="pl-1">
            {items.map(dep => (
              <DependencyItem key={dep.id} dep={dep} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InterfaceDependencyWarning({
  report,
  loading,
  action,
}: InterfaceDependencyWarningProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Scanning dependencies...</span>
      </div>
    );
  }

  if (!report) return null;

  const totalDeps = report.summary.total;
  const cascadeTotal = report.cascadingDependencies?.reduce(
    (sum, c) => sum + c.dependencies.length, 0
  ) || 0;
  const grandTotal = totalDeps + cascadeTotal;

  if (grandTotal === 0) {
    return (
      <p className="text-gray-600 text-sm">
        No dependent configurations found. This interface can be safely {action === 'disable' ? 'disabled' : 'deleted'}.
      </p>
    );
  }

  const actionVerb = action === 'disable' ? 'Disabling' : 'Deleting';
  const actionResult = action === 'delete'
    ? 'The following dependent configurations will be removed:'
    : 'The following configurations depend on this interface and may stop working:';

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      <p className="text-sm text-gray-700">{actionVerb} <strong>{report.interfaceName}</strong> will affect configurations.</p>
      <p className="text-sm text-gray-600">{actionResult}</p>

      {/* Summary badges */}
      <div className="flex space-x-2">
        {report.summary.critical > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            {report.summary.critical + (report.cascadingDependencies?.reduce((s, c) => s + c.dependencies.filter(d => d.severity === 'critical').length, 0) || 0)} critical
          </span>
        )}
        {report.summary.warning > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            {report.summary.warning + (report.cascadingDependencies?.reduce((s, c) => s + c.dependencies.filter(d => d.severity === 'warning').length, 0) || 0)} warning
          </span>
        )}
      </div>

      {/* Direct dependencies */}
      {report.dependencies.length > 0 && (
        <DependencyGroup deps={report.dependencies} />
      )}

      {/* Cascading dependencies (physical interface only) */}
      {report.cascadingDependencies?.map(cascade => (
        cascade.dependencies.length > 0 && (
          <div key={cascade.logicalInterfaceId} className="mt-3 pl-3 border-l-2 border-gray-200">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">
              Dependencies via {cascade.logicalInterfaceName}:
            </h4>
            <DependencyGroup deps={cascade.dependencies} />
          </div>
        )
      ))}
    </div>
  );
}
